// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import LanguageSelector from 'app/components/form/LanguageSelector.tsx';
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  TextField,
  Typography,
  Switch,
  Slider,
  Alert
} from '@mui/material';
import { useSelector } from 'react-redux';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nValidationNs } from '@/i18n/dictionaries/interfaces.ts';

import type { ScenarioSettingsFormProps } from './types.ts';
import * as RecognitionSelector from 'app/selectors/voiceRecognitionSelector.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import { Gender, TtsProvider } from '@/api-gen/api.schemas';
import { createFormField } from 'app/utils/formUtils.ts';

import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';
import { isUserAnyAdminSelector } from 'app/selectors/userSelectors.ts';
import { InfoOutlined, VerifiedUserOutlined, Warning as WarningIcon } from '@mui/icons-material';
import { PdfUploadComponent } from './components/PdfUploadComponent.tsx';
import { PersonaUsage, PersonaUsage as PersonaUsageEnum } from '@/api-gen/api.schemas';
import PersonaSelectionDialog from '../ScenarioPersonasSettings/PersonaSelectionDialog';
import type { PersonaDto } from '@/api-gen/api.schemas';
import { PersonaDropdown } from './PersonaDropdown';
import { useScenarioPersonas } from '../ScenarioPersonasSettings/hooks/useScenarioPersonas';
import PersonaDetailDialog from '../../../PersonasManager/PersonaDetailDialog/PersonaDetailDialog';
import { personaVoiceSignal } from 'app/signals/personaVoiceSignal.ts';

export const ScenarioGeneralSettings: React.FC<ScenarioSettingsFormProps> = ({ scenarioId }) => {

  const [vt] = useTranslation(I18nNamespace.Validation);
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);

  // Create type-safe form field accessor
  const field = createFormField<ScenarioSettingsType>();

  const isDialogActive = useSelector(RecognitionSelector.isDialogActive);

  // State for persona selection dialog
  const [isPersonaDialogOpen, setIsPersonaDialogOpen] = useState(false);
  // State for persona edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPersonaForEdit, setSelectedPersonaForEdit] = useState<PersonaDto | null>(null);

  // Get scenario data first
  const scenario = useSelector(ScenarioSelectors.getScenarioById(scenarioId));

  const { watch, control, formState: { errors, isSubmitted }, getValues, setValue, trigger } = useFormContext<ScenarioSettingsType>();
  const isAnyAdmin = useSelector(isUserAnyAdminSelector);

  const langCode = watch(field.langCode);
  const speedValue = watch(field.speed) ?? 1;

  // Watch scenarioPersonas for changes
  const scenarioPersonas = watch(field.scenarioPersonas) || [];

  // Use shared scenario personas hook to unify logic with Personas tab
  const { defaultPersona, setDefaultPersona, addPersona, hasDefaultPersonaError } = useScenarioPersonas(
    scenarioPersonas,
    setValue,
    trigger,
    translate
  );

  // Handle persona selection from PersonaAutocomplete component
  const handlePersonaChange = useCallback((selectedPersonaId: number | null) => {
    setDefaultPersona(selectedPersonaId);
  }, [setDefaultPersona]);

  // Handle add new persona action from PersonaAutocomplete component
  const handleAddNewPersona = useCallback(() => {
    setIsPersonaDialogOpen(true);
  }, []);

  // Handle edit persona action from PersonaDropdown component
  const handleEditPersona = useCallback((persona: PersonaDto) => {
    setSelectedPersonaForEdit(persona);
    setIsEditDialogOpen(true);
  }, []);

  // Handle adding new persona from dialog (propagate default role if provided)
  const handlePersonaAdd = useCallback((persona: PersonaDto, usages: PersonaUsage[], defaultRoleId?: number) => {
    const result = addPersona(persona, usages, defaultRoleId);
    if (result.success) {
      setIsPersonaDialogOpen(false);
    } else {
      console.error('Failed to add persona:', result.message);
    }
  }, [addPersona]);

  // Keep persona voice signal in sync so other consumers (e.g. response previews) have the active voice configuration
  useEffect(() => {
    const personaVoice = defaultPersona?.persona;

    if (!personaVoice) {
      personaVoiceSignal.value = null;
      return;
    }

    personaVoiceSignal.value = {
      voiceName: personaVoice.voiceName ?? null,
      voiceId: personaVoice.voiceId ?? null,
      sex: personaVoice.gender ?? Gender.Female,
      ttsProvider: personaVoice.ttsProvider ?? TtsProvider.Google,
      langCode: personaVoice.languageCode ?? langCode ?? 'en-US',
      speed: speedValue
    };
  }, [defaultPersona, langCode, speedValue]);


  return (
    <Box sx={{ mt: 5, mb: 3 }}>
      {/* Persona Validation Warning */}
      {isAnyAdmin && isSubmitted && hasDefaultPersonaError && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          {vt(nameof.full<I18nValidationNs>((n) => n.personaValidation.defaultPersonaRequiredGuidance))}
        </Alert>
      )}
      <Grid container>
        <Grid item xs={12}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: fieldProps }) => (
              <TextField
                {...fieldProps}
                value={fieldProps.value ?? ''}
                margin="normal"
                variant="outlined"
                disabled={isDialogActive}
                label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.scenarioName))}
                placeholder={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.scenarioName))}
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={field.description}
            control={control}
            render={({ field: fieldProps }) => (
              <TextField
                {...fieldProps}
                value={fieldProps.value ?? ''}
                margin="normal"
                variant="outlined"
                multiline
                // rows={3}
                maxRows={6}
                disabled={isDialogActive}
                label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.descriptionOptional))}
                placeholder={translate(
                  nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.descriptionOptional)
                )}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* Language, Default Persona, and Voice Speed Row */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <LanguageSelector
              name={nameof.full<ScenarioSettingsType>((f) => f.langCode)}
              control={control}
              label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.language))}
              disabled={isDialogActive}
              error={!!errors.langCode}
              data-cy="listOfLanguage"
            />
          </Grid>

          {/* Default Persona Selection - Only visible to Admin and above */}
          {isAnyAdmin && (() => {
            return (
              <Grid item xs={4}>
                <PersonaDropdown
                  scenarioPersonas={scenarioPersonas}
                  selectedPersona={defaultPersona}
                  usageType={PersonaUsage.Default}
                  label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.defaultPersona))}
                  helperText={
                    scenarioPersonas.length > 0
                      ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.defaultPersonaHelperText))
                      : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.noPersonasAvailable))
                  }
                  placeholder={
                    scenarioPersonas.length === 0
                      ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.noPersonasAvailable))
                      : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.none))
                  }
                  disabled={isDialogActive}
                  onPersonaChange={handlePersonaChange}
                  onAddNewPersona={handleAddNewPersona}
                  onEditPersona={handleEditPersona}
                  showEditButton={true}
                  hasError={isSubmitted && hasDefaultPersonaError}
                  errorMessage={
                    isSubmitted && hasDefaultPersonaError ? vt(nameof.full<I18nValidationNs>((n) => n.personaValidation.exactlyOneDefaultRequired)) : undefined
                  }
                  scenarioLanguage={langCode}
                />
              </Grid>
            );
          })()}

          {/* Voice Speed Slider */}
          <Grid item xs={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '0 1rem' }}>
              <FormControl fullWidth margin="normal">
                <Typography variant="body2" id="voice-speed-slider-label" gutterBottom>
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.voiceSpeed))}
                </Typography>
                <Controller
                  name={field.speed}
                  control={control}
                  render={({ field: fieldProps }) => (
                    <Slider
                      {...fieldProps}
                      aria-labelledby="voice-speed-slider-label"
                      min={0.7}
                      max={1.2}
                      step={0.05}
                      marks
                      valueLabelDisplay="auto"
                      disabled={isDialogActive}
                    />
                  )}
                />
              </FormControl>
            </Box>
          </Grid>
        </Grid>

        {/* Published Status */}
        <Grid container sx={{ mt: 1 }}>
          <Grid item xs={5}>
            <Controller
              name={field.isPublished}
              control={control}
              render={({ field: { value, onChange, ...fieldProps } }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        disabled={isDialogActive}
                        color="primary"
                        {...fieldProps}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {value ? (
                          <VerifiedUserOutlined sx={{ mr: 0.5, fontSize: '1.2rem', color: 'success.main' }} />
                        ) : (
                          <InfoOutlined sx={{ mr: 0.5, fontSize: '1.2rem', color: 'text.secondary' }} />
                        )}
                        <Typography variant="body1" sx={{ mt: 0.4, fontSize: '1.2rem' }} color={value ? 'success.main' : 'text.secondary'}>
                          {value
                            ? (translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.isPublished)) || "Published")
                            : (translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.draft)) || "Draft")}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Box>
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {watch(field.isPublished)
                ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.publishedDescription))
                : translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioGeneralSettings.draftDescription))}
            </Typography>
          </Grid>
        </Grid>

        {/* PDF Upload Section */}
        <PdfUploadComponent scenarioId={scenarioId} hasPdf={scenario?.hasPdf} />

        {/* Persona Selection Dialog - Only for Admin and above */}
        {isAnyAdmin && (
          <PersonaSelectionDialog
            open={isPersonaDialogOpen}
            onClose={() => setIsPersonaDialogOpen(false)}
            onPersonaSelect={(persona, usages, defaultRoleId) => handlePersonaAdd(persona, [PersonaUsage.Default], defaultRoleId)}
            existingPersonaIds={scenarioPersonas.map(sp => sp.personaId)}
            scenarioLanguage={langCode}
          />
        )}

        {/* Persona Detail Dialog - Only for Admin and above */}
        {isAnyAdmin && (
          <PersonaDetailDialog
            open={isEditDialogOpen}
            onClose={() => {
              setIsEditDialogOpen(false);
              setSelectedPersonaForEdit(null);
            }}
            personaId={selectedPersonaForEdit?.id}
            onRefresh={() => {
              // The personas in the scenario are embedded, so we don't need to refresh
              // The PersonaDetailDialog will handle its own data refresh
            }}
          />
        )}
      </Grid>
    </Box>
  );
};

